// Единый источник тон-палитр (разбор 2026-07-23, Фаза 2A).
// Свёл 4 дублирующихся TONE_HEX/STATE_TONE блока (DotStatus, ReadoutAnchor,
// WellnessReadoutAnchor, WellnessReadoutFeed) в одно место.
//
// 2026-07-28: значения — `var(--token)`, а НЕ hex-строки.
//
// Раньше здесь стояли hex-копии токенов, потому что use-sites клеили альфу
// строкой (`${fg}33`) — а `var(--x)33` невалиден. Копия ожидаемо разъехалась:
// канон-сессия 24.07 увела --disc-bike в коралл #F26A3D, здесь остался
// до-канонный #FF7A47, и вело печаталось не тем цветом. Ничего не падало —
// hex валиден, tsc молчит, а тест сверял константу с ней же.
//
// Причина дублирования снята, а не обойдена: альфа теперь берётся через
// color-mix(in srgb, <тон> N%, transparent), который принимает var() и, в
// отличие от hex-строки, СЛЕДУЕТ ТЕМЕ. Это было второй половиной проблемы:
// у --disc-bike есть светлый вариант и три cb-*, плоский hex не шёл ни за
// одним из них — то есть каждая константа была ещё и дырой в a11y-режимах.
//
// Инвариант: в этом файле не должно появляться hex-литералов. Стережёт
// scripts/check_color_tables.py.
import type { DotTone } from "../components/canon/DotStatus";
import type { ReadoutTone } from "../components/canon/ReadoutAnchor";
import type { WellnessReadoutState } from "../components/wellness/WellnessReadoutAnchor";

// Базовые семантические тона — единственная точка правды.
//
// GOOD ссылается на --ok, а НЕ на --active-500: канон-сессия 24.07 вывела
// лайм из роли «хорошо» в пользу okabe #009E73 (colorblind-safe), оставив
// лайм только за disc-run — иначе дисциплина и статус спорят одним hue.
// Здесь GOOD питает DotStatus tone="ok", ReadoutAnchor good и
// WellnessReadoutState high_readiness, то есть ровно роль статуса.
const GOOD = "var(--ok)";
const WARN = "var(--warning-500)";
const BAD = "var(--danger-500)";
const ACCENT = "var(--indigo-500)";
const NEUTRAL = "var(--indigo-300)";
const MUTED = "var(--text-tertiary)";
const DISC_SWIM = "var(--disc-swim)";
const DISC_BIKE = "var(--disc-bike)";
const DISC_RUN = "var(--disc-run)";

/**
 * Полупрозрачный вариант тона.
 *
 * Пришёл на замену конкатенации `${fg}33`: та требовала, чтобы тон был
 * именно hex-строкой, и тем самым запирала весь файл на литералах.
 * color-mix берёт любое CSS-значение цвета, поэтому тон остаётся токеном
 * и переключается вместе с темой.
 */
export function toneAlpha(tone: string, pct: number): string {
  return `color-mix(in srgb, ${tone} ${pct}%, transparent)`;
}

/** Флаговые точки статуса (DotStatus). */
export const DOT_TONE: Record<DotTone, string> = {
  ok: GOOD,
  warn: WARN,
  bad: BAD,
  accent: ACCENT,
  muted: MUTED,
  swim: DISC_SWIM,
  bike: DISC_BIKE,
  run: DISC_RUN,
};

/** Тон-якорь показаний (ReadoutAnchor): fg + мягкий фон. */
export const READOUT_TONE: Record<ReadoutTone, { fg: string; bg: string }> = {
  good: { fg: GOOD, bg: toneAlpha(GOOD, 8) },
  warn: { fg: WARN, bg: toneAlpha(WARN, 8) },
  bad: { fg: BAD, bg: toneAlpha(BAD, 8) },
  neutral: { fg: NEUTRAL, bg: toneAlpha(NEUTRAL, 8) },
  muted: { fg: MUTED, bg: toneAlpha(MUTED, 8) },
};

/** Состояние готовности (WellnessReadoutAnchor + WellnessReadoutFeed — был точный дубль). */
export const WELLNESS_STATE_TONE: Record<WellnessReadoutState, { fg: string; bg: string }> = {
  high_readiness: { fg: GOOD, bg: toneAlpha(GOOD, 8) },
  moderate_readiness: { fg: NEUTRAL, bg: toneAlpha(NEUTRAL, 8) },
  low_readiness: { fg: WARN, bg: toneAlpha(WARN, 8) },
  insufficient_data: { fg: MUTED, bg: toneAlpha(MUTED, 8) },
};
